import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2 } from 'lucide-react';

/**
 * RealDashboardShowcase — Carrousel auto-rotatif des captures de production.
 * 6 écrans : Accueil, Recettes (Léo), Pointage, Évaluation & Primes (Manu),
 * Base Camp gamifié (Sabril), Réseaux Sociaux (Clara).
 *
 * Objectif : casser l'objection "c'est un mockup" en montrant le produit réel
 * avec une animation cinétique (auto-rotation 5s + dots cliquables + agent tag).
 */

const SCREENS = [
  {
    id: 'home',
    src: '/images/audit-resto-dashboard-real.png',
    label: 'Accueil',
    sub: 'Cockpit du jour',
    agent: 'Sabril',
    color: '#F05A1A',
    highlights: ['Météo & alertes', 'CA · couverts · stocks', 'Prime du mois gamifiée'],
  },
  {
    id: 'recettes',
    src: '/images/dashboard-recettes.png',
    label: 'Recettes & Coûts',
    sub: 'Pilotage food cost',
    agent: 'Léo',
    color: '#22C55E',
    highlights: ['Scan fiche manuscrite', 'Food cost en direct', 'Anomalies détectées'],
  },
  {
    id: 'pointage',
    src: '/images/dashboard-pointage.png',
    label: 'Pointage',
    sub: 'Selfie + GPS + uniforme',
    agent: 'Sophie',
    color: '#10B981',
    highlights: ['Selfie validation', 'Position GPS 30m', 'Uniforme vérifié'],
  },
  {
    id: 'primes',
    src: '/images/dashboard-primes.png',
    label: 'Évaluation & Primes',
    sub: 'Méthode coach tacticien',
    agent: 'Manu',
    color: '#F97316',
    highlights: ['4 leviers · 100% stats', 'Carte verte · carton rouge', 'Calculé auto chaque mois'],
  },
  {
    id: 'basecamp',
    src: '/images/dashboard-basecamp.png',
    label: 'Base Camp',
    sub: 'Onboarding gamifié',
    agent: 'Sabril',
    color: '#FBBF24',
    highlights: ['XP · niveaux · badges', '15 niveaux à débloquer', 'Score parfait & champion'],
  },
  {
    id: 'reseaux',
    src: '/images/dashboard-reseaux.png',
    label: 'Réseaux Sociaux',
    sub: 'Générateur viral IA',
    agent: 'Clara',
    color: '#EC4899',
    highlights: ['Scripts IG/TikTok', 'Hook · CTA · hashtags', 'Calendrier de publication'],
  },
];

const AUTO_ROTATE_MS = 5200;

const RealDashboardShowcase = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const screen = SCREENS[idx];

  useEffect(() => {
    if (paused || zoomOpen) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), AUTO_ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, zoomOpen]);

  // Lock body scroll when lightbox open + ESC to close
  useEffect(() => {
    if (!zoomOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setZoomOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomOpen]);

  return (
    <section
      className="relative px-4 sm:px-6 py-14 sm:py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0f0a0a 0%, #1a0e0e 60%, #2a1414 100%)' }}
      data-testid="real-dashboard-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${screen.color} 0%, transparent 70%)`, transition: 'background 0.8s ease' }}
        aria-hidden="true"
      />

      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-9 sm:mb-12">
          <p className="text-[10px] sm:text-xs font-black tracking-[3px] uppercase mb-2" style={{ color: screen.color, transition: 'color 0.6s' }}>
            🟢 6 captures réelles · pas un mockup
          </p>
          <h2 className="font-bebas text-white leading-[0.95] mb-3" style={{ fontSize: 'clamp(34px, 6vw, 60px)' }}>
            VOICI <span style={{ color: screen.color, transition: 'color 0.6s' }}>LE VRAI</span> TABLEAU DE BORD<br/>
            QUE TU PILOTES CHAQUE MATIN.
          </h2>
          <p className="text-white/65 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            Pas une démo embellie. Pas un Figma. <strong className="text-white">C'est ce que tes clients voient en se connectant.</strong>
          </p>
        </div>

        {/* Tabs to pick a screen */}
        <div className="flex gap-1.5 sm:gap-2 mb-5 sm:mb-6 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide" data-testid="dashboard-tabs">
          {SCREENS.map((s, i) => {
            const active = i === idx;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => { setIdx(i); setPaused(true); }}
                className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-full text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all border ${
                  active
                    ? 'text-white shadow-lg scale-105'
                    : 'text-white/55 bg-white/5 border-white/10 hover:text-white hover:bg-white/10'
                }`}
                style={
                  active
                    ? { background: s.color, borderColor: s.color, boxShadow: `0 6px 18px ${s.color}55` }
                    : {}
                }
                data-testid={`dashboard-tab-${s.id}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Browser chrome frame */}
        <motion.div
          layout
          className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.06)] border border-white/10 bg-[#1f1517] relative"
          data-testid="real-dashboard-frame"
        >
          {/* macOS title bar */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#1a1010] border-b border-white/10">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
            <div className="flex-1 flex justify-center">
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/5 border border-white/10">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/65 text-[10px] font-mono">app.audit-resto.fr/{screen.id}</span>
              </div>
            </div>
            <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase whitespace-nowrap">v4.2</span>
          </div>

          {/* Image with crossfade transitions */}
          <div
            className="relative bg-white aspect-video cursor-zoom-in group"
            data-testid="dashboard-frame-inner"
            onClick={() => setZoomOpen(true)}
            role="button"
            tabIndex={0}
            aria-label="Agrandir la capture"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setZoomOpen(true); } }}
          >
            <AnimatePresence mode="sync">
              <motion.img
                key={screen.id}
                src={screen.src}
                alt={`Audit Resto · ${screen.label}`}
                className="absolute inset-0 w-full h-full object-contain object-center"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                loading="lazy"
                data-testid={`dashboard-image-${screen.id}`}
              />
            </AnimatePresence>

            {/* Tap-to-zoom indicator (top-left, opposite of agent tag) */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setZoomOpen(true); }}
              className="absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full backdrop-blur-md bg-black/60 border border-white/20 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-widest hover:bg-black/80 transition-colors z-10"
              data-testid="dashboard-zoom-btn"
              aria-label="Voir en plein écran"
            >
              <Maximize2 className="w-3 h-3" />
              <span className="hidden sm:inline">Plein écran</span>
              <span className="sm:hidden">Tap zoom</span>
            </button>

            {/* Floating agent chip */}
            <motion.div
              key={`${screen.id}-tag`}
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.45, duration: 0.5, ease: 'easeOut' }}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 px-2.5 py-1.5 rounded-full backdrop-blur-md border bg-black/55"
              style={{ borderColor: `${screen.color}80` }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: screen.color }} />
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-white">
                Agent {screen.agent}
              </span>
            </motion.div>
          </div>

          {/* Footer caption — highlights of current screen */}
          <div className="bg-[#15090a] px-4 sm:px-6 py-4 border-t border-white/10">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest mb-1" style={{ color: screen.color, transition: 'color 0.5s' }}>
                  {screen.sub}
                </p>
                <p className="text-white font-bebas text-xl sm:text-2xl leading-none">
                  {screen.label}
                </p>
              </div>
              <div className="hidden sm:flex flex-wrap gap-1.5 justify-end max-w-[55%]">
                {screen.highlights.map((h, i) => (
                  <motion.span
                    key={`${screen.id}-${i}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide text-white/85 bg-white/8 border border-white/12 whitespace-nowrap"
                  >
                    {h}
                  </motion.span>
                ))}
              </div>
            </div>
            {/* Mobile chips on its own row */}
            <div className="flex sm:hidden flex-wrap gap-1.5 mt-3">
              {screen.highlights.map((h, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white/80 bg-white/8 border border-white/10">
                  {h}
                </span>
              ))}
            </div>
          </div>

          {/* Progress bar at very bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/5 overflow-hidden">
            <motion.div
              key={screen.id + (paused ? '-p' : '')}
              initial={{ width: '0%' }}
              animate={{ width: paused ? '100%' : '100%' }}
              transition={paused ? { duration: 0 } : { duration: AUTO_ROTATE_MS / 1000, ease: 'linear' }}
              className="h-full"
              style={{ background: screen.color }}
            />
          </div>
        </motion.div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-5" data-testid="dashboard-dots">
          {SCREENS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Voir ${s.label}`}
              onClick={() => { setIdx(i); setPaused(true); }}
              className={`transition-all rounded-full ${i === idx ? 'w-8 h-2' : 'w-2 h-2 hover:w-3'}`}
              style={{ background: i === idx ? s.color : 'rgba(255,255,255,0.25)' }}
            />
          ))}
        </div>

        <p className="text-center text-white/55 italic text-[12px] sm:text-sm mt-7 sm:mt-9 max-w-xl mx-auto leading-relaxed">
          Chaque resto pilote son propre espace — caisse, fournisseurs, équipe, livraisons connectés. <strong className="text-white not-italic">Tu vois tout. Tu décides tout. Tu scales sans angle mort.</strong>
        </p>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* === LIGHTBOX FULLSCREEN === */}
      <AnimatePresence>
        {zoomOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-sm flex flex-col"
            onClick={() => setZoomOpen(false)}
            data-testid="dashboard-lightbox"
          >
            {/* Top bar */}
            <div
              className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: screen.color }} />
                <p className="text-white font-bebas text-lg sm:text-xl leading-none truncate">
                  {screen.label}
                </p>
                <span className="text-white/50 text-[10px] sm:text-xs uppercase tracking-widest hidden sm:inline">· Agent {screen.agent}</span>
              </div>
              <button
                type="button"
                onClick={() => setZoomOpen(false)}
                className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Fermer"
                data-testid="dashboard-lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable image area — keeps natural resolution */}
            <div
              className="flex-1 overflow-auto px-3 sm:px-8 py-4 sm:py-8 flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={screen.src}
                alt={`Audit Resto · ${screen.label} (plein écran)`}
                className="max-w-none w-auto max-h-full sm:max-w-full sm:h-auto rounded-xl shadow-2xl"
                style={{ minWidth: 'min(900px, 100%)' }}
                data-testid={`dashboard-lightbox-image-${screen.id}`}
              />
            </div>

            {/* Bottom thumbnail strip to switch screens within the lightbox */}
            <div
              className="border-t border-white/10 px-3 sm:px-6 py-3 sm:py-4 overflow-x-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-2 sm:gap-2.5 min-w-min justify-start sm:justify-center" data-testid="dashboard-lightbox-thumbs">
                {SCREENS.map((s, i) => {
                  const active = i === idx;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setIdx(i)}
                      className={`flex-shrink-0 relative rounded-lg overflow-hidden transition-all border-2 ${
                        active ? 'scale-110 shadow-lg' : 'border-white/15 opacity-55 hover:opacity-100'
                      }`}
                      style={{
                        width: 96,
                        height: 54,
                        borderColor: active ? s.color : undefined,
                        boxShadow: active ? `0 6px 18px ${s.color}55` : undefined,
                      }}
                      aria-label={`Voir ${s.label}`}
                      data-testid={`dashboard-lightbox-thumb-${s.id}`}
                    >
                      <img src={s.src} alt="" className="absolute inset-0 w-full h-full object-cover object-top" loading="lazy" />
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default RealDashboardShowcase;
